import moment from "moment";
import {
  APPLICATION_STATUS,
  SYNC_FIELDS_STATUS,
  SYNC_FIELDS_STATUS_DICT,
} from "constants/opekepe";
import { hectaresToSquareMeters } from "utils/fieldDimensions";

const checkApplicationSyncStatus = (application) => {
  return (
    SYNC_FIELDS_STATUS_DICT[application.syncStatus] ===
    SYNC_FIELDS_STATUS.SYNCED
  );
};
export class OpekepeDto {
  static userSync(data) {
    const opekepeSyncData = {
      name: data?.userInfo?.name,
      vat: data?.userInfo?.tin,
      date: moment().toISOString(),
    };
    return opekepeSyncData;
  }

  static fieldsSync(data) {
    const syncedFieldData = {
      status: SYNC_FIELDS_STATUS_DICT[data?.syncStatus],
      totalFields: (data?.numSyncFields || 0) + (data?.numNewFields || 0),
      totalCrops: data?.numUniqueCrops || 0,
      dateSynced: data?.updatedAt
        ? moment(data?.updatedAt).toISOString()
        : moment(data?.createdAt).toISOString(),
    };
    return syncedFieldData;
  }

  static checkFieldsSynced(data, year) {
    const thisYearApplication = data?.find(
      (application) => String(application.year) === String(year)
    );
    let syncedStatus;
    if (!thisYearApplication) {
      syncedStatus = {
        isSynced: false,
      };
    } else {
      syncedStatus = {
        isSynced: checkApplicationSyncStatus(thisYearApplication),
        totalFields:
          (thisYearApplication?.numSyncFields || 0) +
          (thisYearApplication?.numNewFields || 0),
        totalCrops: thisYearApplication?.numUniqueCrops,
        dateSynced: thisYearApplication?.updatedAt
          ? moment(thisYearApplication?.updatedAt).toISOString()
          : moment(thisYearApplication?.createdAt).toISOString(),
      };
    }

    return syncedStatus;
  }

  static checkFarmerSyncStatus(data, year) {
    if (year) {
      const thisYearApplication = data?.find(
        (application) => String(application.year) === String(year)
      );
      if (!thisYearApplication) {
        return {
          year,
          isSynced: false,
        };
      }
      return {
        year,
        isSynced: checkApplicationSyncStatus(thisYearApplication),
      };
    }

    const statusPerYear = data?.map((application) => ({
      year: application.year,
      isSynced: checkApplicationSyncStatus(application),
    }));

    const hasSynced = statusPerYear.some((status) => status.isSynced);

    return { hasSynced, statusPerYear };
  }

  static parseFarmerApplication(data) {
    const applicationStatusCode = data?.applicationStatusCode;

    let applicationStatus = "";
    if (applicationStatusCode in APPLICATION_STATUS) {
      applicationStatus = APPLICATION_STATUS[applicationStatusCode];
    }

    return { ...data, applicationStatus };
  }

  static parseFarmerApplications(data) {
    return data.map(this.parseFarmerApplication);
  }

  static farmerFieldsList(data) {
    const parcels = data.applicationFields
      .filter((parcel) => parcel.imported)
      .map((parcel) => {
        const cultivatedArea = parcel.cultivatedArea ?? parcel.area;
        const ownershipPercent = parcel.ownershipPercent ?? 100;
        return {
          key: `parcel-${parcel.applicationFieldId}`,
          fieldId: parcel.camvioField.id,
          name: parcel.camvioField.name,
          area: (cultivatedArea * ownershipPercent) / 100,
          totalArea: parcel.area,
          cultivatedArea,
          ownershipPercent,
          isSpd: parcel.camvioField.paidField,
          isAppAdded: false,
        };
      });
    const ownFields = data.ownFields.map((field) => ({
      key: `own-${field.id}`,
      fieldId: field.id,
      name: field.name,
      area: field.cultivatedArea ?? field.areaOfField,
      totalArea: field.areaOfField,
      cultivatedArea: field.cultivatedArea ?? field.areaOfField,
      ownershipPercent: 100,
      isSpd: field.paidField,
      isAppAdded: true,
    }));
    return [...parcels, ...ownFields];
  }

  static fieldOpekepeInfo(data) {
    return data?.map((field) => {
      const fieldData = field?.data;
      return {
        area: field?.area,
        atak: fieldData?.fieldPropertyList?.[0]?.atak,
        cartographicBackground:
          fieldData?.fieldGeospatialData?.cartographicBackground,
        cultivars: fieldData?.fieldCultivationList?.map((cul) => ({
          code: cul?.cultivarCode,
          cultivar: cul?.cultivarTitle,
          cultivation: cul?.cultivationTitle,
          area: hectaresToSquareMeters(cul?.totalArea),
        })),
        ecoSchemes:
          fieldData?.fieldEcoschemeSubsidyList
            ?.map((e) => e.ecoschemeSubsidyCode)
            .join(", ") ?? "",
        year: field?.year,
        location: fieldData?.fieldInfo?.location,
        syncedDate: field?.syncedAt
          ? moment(field.syncedAt).format("D MMMM YYYY")
          : field?.syncedAt,
        syncedAt: field?.syncedAt,
      };
    });
  }

  static parseApplicationYears(data) {
    const apps = data?.map((app) => ({
      ...app,
      year: app.year.toString(),
    }));

    const latestActiveYear = apps
      .filter((appYear) => appYear.isActive)
      .reduce(
        (latest, current) => (current.year > latest ? current.year : latest),
        "0"
      );

    return { latestActiveYear, applicationYears: apps };
  }
}
